import { Button, Dropdown, Form, Link, useModal } from '@appsumo/dorado-react';
import {
  faCaretUp,
  faCommentDots,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Transition } from '@headlessui/react';
import {
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldValues } from 'react-hook-form';
import * as yup from 'yup';

import { useUserStatus } from '~/hooks/userStatus';
import {
  DEAL_COMMENT_TYPE,
  DISCUSSION,
  ITEMS_PER_PAGE_ON_AGGREGATE,
  ITEMS_PER_PAGE_ON_DEAL,
} from '~/lib/discussions/constants';
import {
  CommentV2 as CommentV2Type,
  DiscussionFilter,
  DiscussionFilterOption,
  DiscussionSort,
  DiscussionSortOption,
  QuestionV2,
  ReviewV2,
} from '~/lib/discussions/types';
import { PDPContext } from '~/lib/product/context';
import useUser from '~/lib/user';
import type { Deal } from '~/types/deal';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { ChildrenV2, CommentV2, ReplyModalV2 } from '~/components/discussions';
import { Loader } from '~/components/ui';
import { useReviewStats } from '~/hooks/userReviewStats';
import { getCommentsV2, getDiscussionsUrlV2 } from '~/lib/discussions/fetch';
import useDiscussionInfinitePagination from '~/lib/discussions/useDiscussionInfinitePagination';
import { useEvent, useEventDispatch } from '~/lib/events';
import { useExperiment } from '~/lib/experiment';
import { getHashFromPath, getQueryFromPath } from '~/lib/util';
import {
  LEAVE_REVIEW_PAGE_EXPERIMENT_NAME,
  LOGIN_URL,
  QUESTIONS_2_5_UI_EXPERIMENT_NAME,
  REVIEWS_2_5_UI_EXPERIMENT_NAME,
  isAggregatePage,
  isPDP,
} from '~/lib/util/constants';
import QuestionList from '../discussions/QuestionList';
import { ReviewList } from '../discussions/ReviewList';
import InformationBox from '../ui/InformationBox';
import Pagination from '../ui/Pagination';
import { BuyNow } from './PricingCard/BuyNow';

/**
 * Replies to a comment (thread) allowing children to hide/show.
 */
export function CommentThread({
  comment,
  mutateComment,
  moderator = false,
  showAllReplies = true,
  isReview = false,
}: {
  comment: CommentV2Type;
  mutateComment: () => void;
  moderator?: boolean;
  showAllReplies: boolean;
  isReview?: boolean;
}) {
  const [showReplies, setShowReplies] = useState(showAllReplies);

  // when the show all/hide all replies changes update this node
  useEffect(() => {
    setShowReplies(showAllReplies);
  }, [showAllReplies]);

  const replyCount = useMemo(() => {
    const typedComment = comment as CommentV2Type;

    return (
      (typedComment.children ? typedComment.children.length : 0) +
      (typedComment.children
        ? (typedComment.children as CommentV2Type[]).reduce(
            (sum, child: CommentV2Type) =>
              sum +
              ((child.children as CommentV2Type[] | undefined)?.length ?? 0),
            0,
          )
        : 0)
    );
  }, [comment]);

  return (
    <div key={comment.id}>
      <div className="rounded-lg shadow-lg">
        <CommentV2
          href={comment.display_path}
          comment={comment as CommentV2Type}
          moderator={moderator}
          mutateComment={mutateComment}
          isReview={isReview}
        />
        {comment.children && comment.children.length > 0 && (
          <div className="border-t bg-gray-100 p-4 sm:px-12">
            <div className="flex items-end justify-end">
              <button
                className="group inline-flex items-center gap-x-2 text-xs text-gray-500"
                onClick={() => setShowReplies(!showReplies)}
              >
                <FontAwesomeIcon
                  icon={faCaretUp}
                  className={`${showReplies ? '' : 'rotate-180'}`}
                />
                {showReplies ? 'Hide' : 'Show'} replies
                <div className="h-5 w-5 rounded-full border border-gray-500 text-center align-middle text-xs transition delay-150 ease-in-out group-hover:border-yellow-500 group-hover:text-yellow-500">
                  {replyCount}
                </div>
              </button>
            </div>
            <Transition
              show={showReplies}
              enter="transition-[max-height] duration-300 ease-in-out overflow-hidden"
              enterFrom="max-h-[0px]"
              enterTo="max-h-none"
              leave="transition-[max-height] duration-300 ease-in-out overflow-hidden"
              leaveFrom="max-h-none"
              leaveTo="max-h-[0px]"
            >
              <ChildrenV2
                root={comment as CommentV2Type}
                comments={comment.children as CommentV2Type[]}
                moderator={moderator}
                mutateComment={mutateComment}
                isReview={isReview}
              />
            </Transition>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginToComment({
  dealSlug,
  actionText,
}: Readonly<{
  dealSlug: string;
  actionText: string;
}>) {
  let next = window?.location.pathname;

  const { variant } = useExperiment(LEAVE_REVIEW_PAGE_EXPERIMENT_NAME);

  if (variant === 'enabled') {
    next = `/products/${dealSlug}/reviews/new`;
  }

  return (
    <InformationBox
      text={`Log in to ${actionText}`}
      helpText="Only logged-in purchasers can review products on AppSumo."
    >
      <Button href={`${LOGIN_URL}?next=${next}`} primary>
        Log in
      </Button>
    </InformationBox>
  );
}

function VerifyToComment({ actionText }: Readonly<{ actionText: string }>) {
  return (
    <InformationBox
      text={`Verify your email to ${actionText}`}
      helpText="Email verification helps us maintain a trustworthy review system."
    >
      <Button href="/account/email/" tertiary>
        Verify
      </Button>
    </InformationBox>
  );
}

function BuyToComment({
  deal,
  actionText,
  push,
  isPDPPage,
}: Readonly<{
  deal: Deal;
  actionText: string;
  push: (url: string) => void;
  isPDPPage: boolean;
}>) {
  useEvent('cart:added', () => {
    // we have a 'cart:added' handler on PDP, let's not duplicate here
    if (!isPDPPage) {
      push('/cart/');
    }
  });

  return (
    <InformationBox
      text={`Buy now to ${actionText}`}
      helpText="Only verified purchasers can review products on AppSumo."
    >
      {/* InView = true to not display sticky banner on mobile */}
      <BuyNow deal={deal} inView hideAdditionalInformation />
    </InformationBox>
  );
}

function EmptyCommentsCard({
  type,
  deal,
  onClick,
  disabled,
  isSearching,
}: {
  type: string;
  deal: Deal;
  onClick: () => void;
  disabled: boolean;
  isSearching: boolean;
}) {
  let header;
  let description;

  if (!isSearching) {
    // no comments
    header =
      type === DEAL_COMMENT_TYPE.REVIEW
        ? 'No one has reviewed this product... yet'
        : 'No one has asked a question... yet';
    description =
      type === DEAL_COMMENT_TYPE.REVIEW
        ? 'Be the first to write a review. (Purchase required.)'
        : `Be the first to ask a question.  Get a reply from the ${deal.public_name} team.`;
  } else {
    // no search results
    header =
      type === DEAL_COMMENT_TYPE.REVIEW
        ? 'Hmm, no one has mentioned that... yet'
        : "A question so good, it hasn't been asked... yet";
    description =
      type === DEAL_COMMENT_TYPE.REVIEW
        ? 'Write a review now. (Purchase required.)'
        : `Ask the ${deal.public_name} team now to get answers.`;
  }

  const buttonText =
    type === DEAL_COMMENT_TYPE.REVIEW ? 'Write a review' : 'Ask a question';

  return (
    <div className="flex flex-col gap-4 rounded border border-gravel p-6 shadow-md">
      <h4 className="font-header text-3xl font-bold">{header}</h4>
      <p>{description}</p>
      <div>
        <Button disabled={disabled} onClick={onClick} secondary>
          {buttonText}
        </Button>
      </div>
    </div>
  );
}

const filterOptions: DiscussionFilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'unapproved', label: 'Pending' },
];

const getFilterOptionsNewUI = (
  type: DEAL_COMMENT_TYPE,
): DiscussionFilterOption[] => {
  const filterOptionsNewUI: DiscussionFilterOption[] = [
    { value: 'all', label: 'All' },
    { value: 'needs_reply', label: 'Needs reply' },
    { value: 'unapproved', label: 'Needs moderation' },
  ];

  if (type === DEAL_COMMENT_TYPE.QUESTION) {
    filterOptionsNewUI[1].label = 'Needs answer';
  }

  return filterOptionsNewUI;
};

function MoreCommentsCTA({
  commentType,
  queryParams,
}: {
  commentType: DEAL_COMMENT_TYPE;
  queryParams?: URLSearchParams;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  const {
    deal: { slug: dealSlug },
  }: { deal: Deal } = useContext(PDPContext);

  const textType =
    commentType === DEAL_COMMENT_TYPE.REVIEW ? 'reviews' : 'questions';

  const params = new URLSearchParams(queryParams);
  params?.append('ref', 'pdp');

  return (
    <Link
      className="mt-2 rounded-full bg-yellow-400 px-4 py-2 text-xs font-medium"
      href={`/products/${dealSlug}/${textType}?${params.toString()}`}
      target="_blank"
    >
      See all {textType}
    </Link>
  );
}

/**
 * Render a stream of comments or deal reviews on a PDP tab.
 */
export function DealComments({
  dealId,
  fallbackData,
  type,
  sortOptions,
  total,
  updateTotal,
  query,
  enableParamsState = false,
  enableManualPagination = false,
  paginationHook = useDiscussionInfinitePagination,
}: Readonly<{
  dealId: number;
  fallbackData: any[];
  type: DEAL_COMMENT_TYPE;
  sortOptions: DiscussionSortOption[];
  total: number;
  updateTotal: (total: number) => void;
  query?: string;
  enableParamsState?: boolean;
  enableManualPagination?: boolean;
  paginationHook?: Function;
}>) {
  const dispatchEvent = useEventDispatch();
  const { deal }: { deal: Deal } = useContext(PDPContext);
  const { user } = useUser();
  const { userStatus } = useUserStatus(dealId);
  const { reviewStats } = useReviewStats(dealId);

  // current sort value
  const [sortBy, setSortBy] = useState<DiscussionSort>(sortOptions[0].value);
  // current filter value
  const [filterBy, setFilterBy] = useState<string>(filterOptions[0].value);

  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);

  const { asPath, push } = useRouter();

  const prevPageRef = useRef<number>(1);
  const formRef = useRef<HTMLFormElement>(null);

  // TODO: Remove once discussion experiments are clean up,
  // this is to prevent hydration mismatch due to multiple experiments conditions
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Set the state to true once the component is mounted on the client side
    setIsClient(true);
  }, []);

  // sortOptions can change for Partner users, so we set the new default value if it changes
  useEffect(() => {
    setSortBy(sortOptions[0].value);
    formRef?.current?.setValue('sort', sortOptions[0]);
  }, [sortOptions]);

  const { variant: leaveReviewVariant } = useExperiment(
    LEAVE_REVIEW_PAGE_EXPERIMENT_NAME,
  );

  const leaveReviewPageIsEnable = leaveReviewVariant === 'enabled';

  const [dynamicFilterOptions, setDynamicFilterOptions] =
    useState<DiscussionFilterOption[]>(filterOptions);

  // holds the state for show all/hide all replies
  const [showReplies, setShowReplies] = useState(true);

  // load comments using given hook
  const {
    data,
    size,
    setSize,
    error,
    mutate,
    isLoadingMore,
    isFinishedPaginating,
  } = paginationHook({
    dealId,
    type,
    sortBy,
    filterBy,
    query,
    paginateOnSearch: true,
    discussionUrlFn: getDiscussionsUrlV2,
    discussionFetcher: getCommentsV2,
    fallbackData,
  });

  useEffect(() => {
    if ((isLoadingInitialData && data?.length && !isLoadingMore) || !!error) {
      setIsLoadingInitialData(false);
    }
  }, [data, error, isLoadingInitialData, isLoadingMore]);

  const [showModal, hideModal] = useModal(() => {
    return (
      <ReplyModalV2
        hideModal={hideModal}
        deal={deal}
        type={
          type === DEAL_COMMENT_TYPE.QUESTION ? 'dealcomment' : 'dealreview'
        }
        mutateComment={mutate}
      />
    );
  }, []);

  const showModalAndEmit = useCallback(() => {
    showModal();
    dispatchEvent('pdp:toggleBuyButton', true);
  }, [showModal, dispatchEvent]);

  function onCreateDiscussion() {
    if (type === DEAL_COMMENT_TYPE.QUESTION || !leaveReviewPageIsEnable) {
      showModalAndEmit();
      return;
    }

    push(`/products/${deal.slug}/reviews/new`);
  }

  const isSearching = !!query;

  // construct data for rendering by combing all pages into a single stream of comments
  const comments: CommentV2Type[] = useMemo(() => {
    if (
      enableManualPagination &&
      typeof window !== 'undefined' &&
      prevPageRef.current !== size
    ) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        prevPageRef.current = size;
      }, 50);
    }

    return data?.comments || [];
  }, [enableManualPagination, size, data]);

  const { total: commentsTotal } = useMemo(() => {
    return data?.meta || {};
  }, [data?.meta]);

  // experiment is enrolled in the API call to get comments
  const showNewUI = useMemo(() => {
    // check safe null first, since doing it directly calling the prop throws an error
    if (!data?.meta) {
      return false;
    }

    if (type === DEAL_COMMENT_TYPE.REVIEW) {
      return (
        Object.prototype.hasOwnProperty.call(
          data.meta,
          REVIEWS_2_5_UI_EXPERIMENT_NAME,
        ) && data.meta[REVIEWS_2_5_UI_EXPERIMENT_NAME] === 'enabled'
      );
    }

    return (
      Object.prototype.hasOwnProperty.call(
        data.meta,
        QUESTIONS_2_5_UI_EXPERIMENT_NAME,
      ) && data.meta[QUESTIONS_2_5_UI_EXPERIMENT_NAME] === 'enabled'
    );
  }, [data?.meta, type]);

  const {
    needs_moderation: needsModeration,
    needs_reply: needsReply,
    review_details: { review_count: reviewCount = 0 } = {},
  } = useMemo(() => {
    return reviewStats || {};
  }, [reviewStats]);

  // update total comments
  useEffect(() => {
    if (isSearching) {
      updateTotal(comments.length);
    }
  }, [comments, isSearching, updateTotal]);

  // set sort based on form.dropdown internal change
  function onFormChange(data: FieldValues, name: string, type: string) {
    if (name === 'sort' && type === 'change') {
      setSortBy(data.sort.value as DiscussionSort);
    }
  }

  // current sort label
  const currentSort = useMemo(() => {
    const sort = sortOptions.find(
      (f: DiscussionSortOption) => f.value === sortBy,
    ) as DiscussionSortOption;
    return sort.label;
  }, [sortBy, sortOptions]);

  // current filter label
  const currentFilter = useMemo(() => {
    if (!dynamicFilterOptions) {
      return '';
    }

    const filter = dynamicFilterOptions.find(
      (f: DiscussionFilterOption) => f.value === filterBy,
    ) as DiscussionFilterOption;

    return filter?.label;
  }, [dynamicFilterOptions, filterBy]);

  const isEmptyComments = comments.length === 0 && !isLoadingMore;

  useEffect(() => {
    if ((isLoadingInitialData && data?.length && !error) || isEmptyComments) {
      setIsLoadingInitialData(false);
    }
  }, [data, error, isLoadingInitialData, isEmptyComments]);

  const statusCounts = useMemo(() => {
    let unresolved = 0;
    let pending = 0;

    const traverseComments = (comments: CommentV2Type[]) => {
      comments.forEach((comment: CommentV2Type) => {
        if (!comment.resolved) {
          unresolved++;
        }

        if (comment.approved === null) {
          pending++;
        }

        if (comment.children?.length) {
          traverseComments(comment?.children);
        }
      });
    };

    traverseComments(comments);

    return {
      unresolved,
      pending,
    };
  }, [comments]);

  const allowCreateItem = useMemo(() => {
    // unauthenticated users can't do anything
    if (!user) {
      return false;
    }

    // need to purchase to write a review
    if (type === DEAL_COMMENT_TYPE.REVIEW) {
      return !!userStatus?.user_has_purchased;
    }

    return true;
  }, [type, userStatus, user]);

  useEffect(() => {
    const hash = getHashFromPath(asPath);
    const params = getQueryFromPath(asPath);

    if (params.has('sort')) {
      setSortBy(params.get('sort') as DiscussionSort);
    }

    if (params.has('filter')) {
      setFilterBy(params.get('filter') as DiscussionFilter);
    }

    const rating = Number(params.get('rating'));
    if (
      type === DEAL_COMMENT_TYPE.REVIEW &&
      hash === DISCUSSION.REVIEWS.toString() &&
      allowCreateItem &&
      rating >= 1 &&
      rating <= 5
    ) {
      if (leaveReviewPageIsEnable) {
        push(`/products/${deal.slug}/reviews/new?rating=${rating}`);
        return;
      }

      showModalAndEmit();
    }
  }, [
    allowCreateItem,
    asPath,
    deal.slug,
    push,
    showModal,
    type,
    leaveReviewPageIsEnable,
    showModalAndEmit,
  ]);

  useEffect(() => {
    if (enableParamsState) {
      const url = new URL(window.location.href);

      if (filterBy !== filterOptions[0].value) {
        url.searchParams.set('filter', filterBy);
      } else {
        url.searchParams.delete('filter');
      }
      if (sortBy !== sortOptions[0].value) {
        url.searchParams.set('sort', sortBy);
      } else {
        url.searchParams.delete('sort');
      }

      window.history.replaceState({}, '', url.toString());
    }
  }, [sortBy, filterBy, enableParamsState, sortOptions]);

  const isEmailVerified = true; // !!user?.email_verified;

  // set up params for aggregate page
  const aggregateParams = useMemo(() => {
    const params = new URLSearchParams();

    if (query) {
      params.set('query', query);
    }

    // not sending first option as it's default
    if (sortBy !== sortOptions[0].value) {
      params.set('sort', sortBy);
    }

    // not sending first option as it's default
    if (filterBy !== filterOptions[0].value) {
      params.set('filter', filterBy);
    }

    return params;
  }, [filterBy, query, sortBy, sortOptions]);

  useEffect(() => {
    if (enableManualPagination) {
      setSize(1);
    }
  }, [query, enableManualPagination, filterBy, setSize]);

  const isPartner = useMemo(() => {
    return deal.partner_user_ids.includes(user?.id || 0);
  }, [deal.partner_user_ids, user?.id]);

  useEffect(() => {
    if (showNewUI && userStatus?.user_moderator) {
      setDynamicFilterOptions(getFilterOptionsNewUI(type));
    }
  }, [showNewUI, type, userStatus?.user_moderator]);

  if (!isClient) {
    return (
      <div className="my-5 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const showFilters = showNewUI ? reviewCount : total;

  const onChangePage = (page: number) => {
    prevPageRef.current = size;
    setSize(page);
  };

  const itemsPerPage = isAggregatePage(asPath.split('?')[0])
    ? ITEMS_PER_PAGE_ON_AGGREGATE
    : ITEMS_PER_PAGE_ON_DEAL;

  const isPDPPage = isPDP(asPath);

  const commentActionText =
    type === DEAL_COMMENT_TYPE.QUESTION ? 'ask a question' : 'leave a review';

  return (
    <>
      {/* Filters */}
      {!!showFilters && (
        <div className="flex w-full items-center max-sm:flex-col max-sm:items-start max-sm:gap-y-4 sm:gap-x-4">
          <div className="flex max-sm:w-full max-sm:flex-col max-sm:gap-y-4">
            {isClient && currentFilter && userStatus?.user_moderator && (
              <Dropdown
                labelPrefix="Filter by:"
                defaultValue={{ label: currentFilter, value: filterBy }}
                options={dynamicFilterOptions}
                onClick={(value) =>
                  setFilterBy(value.value as DiscussionFilter)
                }
                className="mr-4 text-xs max-sm:w-full md:w-32"
              />
            )}
            {/* 
              We display sort only if user hook has finish. (user will be an object for logged in, and null for anon).
              Using Dorado Form to set the sort value manually after first render and watch for changes.
            */}
            <Form
              ref={formRef}
              onSubmit={() => {}}
              resolver={yupResolver(
                yup.object().shape({
                  sort: yup.string().required(),
                  notify: yup.boolean(),
                }),
              )}
              watch={onFormChange}
            >
              <Form.Dropdown
                labelPrefix="Sort by:"
                name="sort"
                defaultValue={{ label: currentSort, value: sortBy }}
                options={sortOptions}
                className="text-xs max-sm:w-full md:w-32"
              />
            </Form>
          </div>
          <div className="flex grow justify-between max-sm:w-full">
            {showNewUI && userStatus?.user_moderator && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="text-sm text-blue-600"
              >
                {showReplies ? 'Hide' : 'Show'} all replies
              </button>
            )}

            {!showNewUI && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="text-sm text-blue-600"
              >
                {showReplies ? 'Hide' : 'Show'} all replies
              </button>
            )}

            {!showNewUI && enableManualPagination && comments.length > 0 && (
              <div>
                Showing <strong>{(size - 1) * itemsPerPage + 1}</strong> to{' '}
                <strong>{Math.min(size * itemsPerPage, total)} </strong>
                of <strong>{total}</strong>{' '}
                {type === 'dealcomment' ? 'questions' : 'reviews'}
              </div>
            )}
            {!showNewUI && !enableManualPagination && comments.length > 0 && (
              <div>
                Showing <strong>{comments.length}</strong> of{' '}
                <strong>{isSearching ? comments.length : total}</strong>{' '}
                {type === 'dealcomment' ? 'questions' : 'reviews'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Information box based on user info */}
      {typeof user !== 'undefined' && (
        <>
          {!user?.id && (
            <div>
              <LoginToComment
                dealSlug={deal.slug}
                actionText={commentActionText}
              />
            </div>
          )}
          {!!user?.id && !user?.email_verified && !user?.is_staff && (
            <div>
              <VerifyToComment actionText={commentActionText} />
            </div>
          )}
          {/* We don't display BuyNow box for info or inactive deals */}
          {type === DEAL_COMMENT_TYPE.REVIEW &&
            !!user?.id &&
            user?.email_verified &&
            !userStatus?.user_has_purchased &&
            !(deal.is_info || deal.stage_details?.is_inactive_indefinitely) && (
              <div>
                <BuyToComment
                  actionText={commentActionText}
                  deal={deal}
                  push={push}
                  isPDPPage={isPDPPage}
                />
              </div>
            )}
        </>
      )}

      {/* Moderator information - old UI  */}
      {!showNewUI && userStatus?.user_moderator && (
        <div>
          <span className="text-sm">
            Unresolved: <strong>{statusCounts.unresolved}</strong>
          </span>
          <span className="ml-3 text-sm">
            Pending Moderation: <strong>{statusCounts.pending}</strong>
          </span>
        </div>
      )}

      {/* Moderator information - new UI  */}
      {showNewUI && userStatus?.user_moderator && (
        <div className="flex">
          {/* Partners and staff moderator */}
          {(isPartner || userStatus?.staff_moderator) && !!needsReply && (
            <div>
              <FontAwesomeIcon
                icon={faCommentDots}
                className="mr-1 text-base text-brick"
              />
              <span className="text-sm">
                {needsReply} needs{' '}
                {type === DEAL_COMMENT_TYPE.REVIEW ? 'reply' : 'answer'}
              </span>
            </div>
          )}

          {!!needsReply && !!needsModeration && (
            <span className="mx-2 text-slate">|</span>
          )}

          {userStatus?.staff_moderator && !!needsModeration && (
            <div>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mr-1 text-base text-dorado"
              />
              <span className="text-sm">
                {needsModeration} needs moderation
              </span>
            </div>
          )}
        </div>
      )}

      {/* Results counter info - Manual pagination */}
      {showNewUI && enableManualPagination && comments.length > 0 && (
        <div
          className={[
            (isPartner || userStatus?.staff_moderator) && '-mt-4',
            'text-sm',
          ].join(' ')}
        >
          Showing {(size - 1) * itemsPerPage + 1}-
          {Math.min(size * itemsPerPage, commentsTotal)} of&nbsp;
          {commentsTotal} {type === 'dealcomment' ? 'questions' : 'reviews'}
        </div>
      )}

      {/* Results counter info - Scroll pagination */}
      {showNewUI && !enableManualPagination && comments.length > 0 && (
        <div
          className={[
            (isPartner || userStatus?.staff_moderator) && '-mt-4',
            'text-sm',
          ].join(' ')}
        >
          Showing {(size - 1) * itemsPerPage + 1}-
          {Math.min(size * itemsPerPage, commentsTotal)} of&nbsp;
          {commentsTotal} {type === 'dealcomment' ? 'questions' : 'reviews'}
        </div>
      )}

      {/* CTA new discussion  */}
      {(user?.email_verified || user?.is_staff) && !isEmptyComments && (
        <div>
          <Button
            onClick={() => onCreateDiscussion()}
            disabled={
              !allowCreateItem ||
              !isEmailVerified ||
              (userStatus?.user_has_reviewed && type === 'dealreview') ||
              user.comment_blacklist
            }
            secondary
          >
            {type === 'dealcomment' ? 'Ask a question' : 'Write a review'}
          </Button>
        </div>
      )}

      {/* No results fallback */}
      {isEmptyComments && !isLoadingMore && (
        <EmptyCommentsCard
          type={type}
          deal={deal}
          onClick={() => onCreateDiscussion()}
          disabled={
            !allowCreateItem ||
            !isEmailVerified ||
            !!user?.comment_blacklist ||
            (userStatus?.user_has_reviewed && type === 'dealreview')
          }
          isSearching={isSearching}
        />
      )}

      {/* Results */}
      {(isLoadingMore || (!!comments.length && !isLoadingMore)) && (
        <div className="flex flex-col gap-y-5 sm:gap-y-10">
          {!showNewUI &&
            comments.map((comment: CommentV2Type) => (
              <CommentThread
                key={comment.id}
                comment={comment}
                showAllReplies={showReplies}
                moderator={userStatus?.user_moderator}
                mutateComment={mutate}
                isReview={type === DEAL_COMMENT_TYPE.REVIEW}
              />
            ))}
          {showNewUI && type === DEAL_COMMENT_TYPE.QUESTION && (
            <QuestionList
              isModerator={!!userStatus?.user_moderator}
              questions={comments as QuestionV2[]}
              showAllReplies={showReplies}
            />
          )}
          {showNewUI && type === DEAL_COMMENT_TYPE.REVIEW && (
            <ReviewList
              isModerator={!!userStatus?.user_moderator}
              reviews={comments as ReviewV2[]}
              showAllReplies={showReplies}
            />
          )}
        </div>
      )}

      {/* Scroll Pagination  */}
      {!enableManualPagination &&
        !isLoadingMore &&
        !isSearching &&
        !isFinishedPaginating && (
          <div className="my-5 flex items-center justify-center">
            <MoreCommentsCTA
              commentType={type}
              queryParams={aggregateParams}
              onClick={() => setSize(size + 1)}
            />
          </div>
        )}

      {/* Manual pagination  */}
      {enableManualPagination &&
        commentsTotal > itemsPerPage &&
        comments.length > 0 && (
          <div className="text-center">
            <Pagination
              itemsPerPage={itemsPerPage}
              currentPage={size}
              onChangePage={onChangePage}
              totalItems={commentsTotal}
              disabled={isLoadingMore}
            />
          </div>
        )}
    </>
  );
}
